import { FunctionComponent, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonIcon from '@mui/icons-material/Person';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import MedicationIcon from '@mui/icons-material/Medication';
import ArticleIcon from '@mui/icons-material/Article';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import StarIcon from '@mui/icons-material/Star';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import ShareIcon from '@mui/icons-material/Share';
import { useSavedItems } from '../../../contexts/SavedItemsContext';
import { savedApi, ApiError } from '../../../services/api';
import styles from './ListDetail.module.css';

interface ListData {
  id: string;
  title: string;
  description: string;
  lastUpdated: string;
  createdBy?: string;
  isPublic?: boolean;
  categories: {
    providers: any[];
    procedures: any[];
    medications: any[];
    articles: any[];
  };
}

const ListDetail: FunctionComponent = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { saveItem, removeItem, isItemSaved } = useSavedItems();
  const [listData, setListData] = useState<ListData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<string>('all');

  useEffect(() => {
    const fetchListData = async () => {
      if (!id) return;
      
      setIsLoading(true);
      try {
        const result = await savedApi.getListById(id);
        setListData(result);
      } catch (error) {
        console.error('Error fetching list:', error);
        if (error instanceof ApiError) {
          console.error('API Error:', error.message);
        }
        // Optionally set an error state here
      } finally {
        setIsLoading(false);
      }
    };

    fetchListData();
  }, [id]);

  const isSaved = listData ? isItemSaved(listData.id, 'list') : false;

  const handleSave = () => {
    if (!listData) return;
    
    if (isSaved) {
      removeItem(listData.id, 'list');
    } else {
      saveItem({
        id: listData.id,
        type: 'list',
        title: listData.title,
        subtitle: `${getTotalItems()} items`,
        savedAt: new Date(),
        data: listData
      });
    }
  };

  const handleShare = async () => {
    if (navigator.share && listData) {
      try {
        await navigator.share({
          title: listData.title,
          text: listData.description,
          url: window.location.href
        });
      } catch (error) {
        // User cancelled the share dialog or an error occurred
        // Silently handle the error - no need to show anything to the user
        if ((error as Error).name !== 'AbortError') {
          console.error('Error sharing:', error);
        }
      }
    } else if (!navigator.share) {
      // Fallback for browsers that don't support native share
      console.log('Web Share API not supported');
      // Could implement a custom share modal here
    }
  };

  const getTotalItems = () => {
    if (!listData) return 0;
    return Object.values(listData.categories).reduce((sum, arr) => sum + arr.length, 0);
  };

  const getFilteredItems = () => {
    if (!listData) return [];
    
    if (activeTab === 'all') {
      return Object.entries(listData.categories).flatMap(([category, items]) => 
        items.map(item => ({ ...item, category }))
      );
    }
    
    return listData.categories[activeTab as keyof typeof listData.categories] || [];
  };

  const handleProviderClick = (provider: any) => {
    navigate(`/provider/${provider.id}`);
  };

  const handleProcedureClick = (procedure: any) => {
    navigate(`/procedure/${procedure.id}`);
  };

  const handleMedicationClick = (medication: any) => {
    navigate(`/medication/${medication.id}`);
  };

  const renderProviderCard = (provider: any) => (
    <div 
      key={provider.id} 
      className={styles.card}
      onClick={() => handleProviderClick(provider)}
    >
      <div className={styles.cardHeader}>
        <PersonIcon className={styles.cardIcon} />
        <div className={styles.cardInfo}>
          <h3 className={styles.cardTitle}>{provider.name}</h3>
          <p className={styles.cardSubtitle}>{provider.specialty}</p>
        </div>
      </div>
      <div className={styles.cardDetails}>
        <div className={styles.detailItem}>
          <LocationOnIcon className={styles.detailIcon} />
          <span>{provider.distance || provider.address}</span>
        </div>
        {provider.rating && (
          <div className={styles.detailItem}>
            <StarIcon className={styles.detailIcon} />
            <span>{provider.rating} ({provider.reviewCount} reviews)</span>
          </div>
        )}
        {provider.nextAvailable && (
          <div className={styles.detailItem}>
            <AccessTimeIcon className={styles.detailIcon} />
            <span>Next: {provider.nextAvailable}</span>
          </div>
        )}
      </div>
    </div>
  );

  const renderProcedureCard = (procedure: any) => (
    <div 
      key={procedure.id} 
      className={styles.card}
      onClick={() => handleProcedureClick(procedure)}
    >
      <div className={styles.cardHeader}>
        <MedicalServicesIcon className={styles.cardIcon} />
        <div className={styles.cardInfo}>
          <h3 className={styles.cardTitle}>{procedure.name}</h3>
          <p className={styles.cardSubtitle}>{procedure.category} • CPT: {procedure.cpt}</p>
        </div>
      </div>
      <div className={styles.cardDetails}>
        <div className={styles.detailItem}>
          <span className={styles.price}>{procedure.price || `$${procedure.averagePrice}`}</span>
        </div>
        <div className={styles.detailItem}>
          <span className={styles.coverage}>{procedure.insuranceCoverage}</span>
        </div>
      </div>
    </div>
  );

  const renderMedicationCard = (medication: any) => (
    <div 
      key={medication.id} 
      className={styles.card}
      onClick={() => handleMedicationClick(medication)}
    >
      <div className={styles.cardHeader}>
        <MedicationIcon className={styles.cardIcon} />
        <div className={styles.cardInfo}>
          <h3 className={styles.cardTitle}>{medication.name}</h3>
          <p className={styles.cardSubtitle}>{medication.genericName} • {medication.category}</p>
        </div>
      </div>
      <div className={styles.cardDetails}>
        <div className={styles.detailItem}>
          <span className={styles.price}>{medication.price || `$${medication.averagePrice}/month`}</span>
        </div>
        <div className={styles.detailItem}>
          <span className={styles.coverage}>{medication.insuranceCoverage}</span>
        </div>
        {medication.commonDosages && (
          <div className={styles.detailItem}>
            <span className={styles.dosages}>Dosages: {medication.commonDosages.join(', ')}</span>
          </div>
        )}
      </div>
    </div>
  );

  const renderArticleCard = (article: any) => (
    <div key={article.id} className={styles.card}>
      <div className={styles.cardHeader}>
        <ArticleIcon className={styles.cardIcon} />
        <div className={styles.cardInfo}>
          <h3 className={styles.cardTitle}>{article.title}</h3>
          <p className={styles.cardSubtitle}>By {article.author} • {article.readTime}</p>
        </div>
      </div>
      {article.preview && (
        <div className={styles.cardDetails}>
          <p className={styles.articlePreview}>{article.preview}</p>
        </div>
      )}
      <div className={styles.articleMeta}>
        <span>{article.publishedDate}</span>
      </div>
    </div>
  );

  const renderCard = (item: any) => {
    switch (item.category || activeTab) {
      case 'providers':
        return renderProviderCard(item);
      case 'procedures':
        return renderProcedureCard(item);
      case 'medications':
        return renderMedicationCard(item);
      case 'articles':
        return renderArticleCard(item);
      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <div className={styles.listDetail}>
        <div className={styles.loading}>
          <div className={styles.spinner}></div>
          <p>Loading list...</p>
        </div>
      </div>
    );
  }

  if (!listData) {
    return (
      <div className={styles.listDetail}>
        <div className={styles.notFound}>
          <h2>List not found</h2>
          <button onClick={() => navigate(-1)}>Go back</button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.listDetail}>
      <div className={styles.header}>
        <button className={styles.backButton} onClick={() => navigate(-1)}>
          <ArrowBackIcon />
        </button>
        <div className={styles.headerContent}>
          <h1 className={styles.listTitle}>{listData.title}</h1>
          <p className={styles.itemCount}>{getTotalItems()} items</p>
        </div>
        <div className={styles.headerActions}>
          <button className={styles.actionButton} onClick={handleSave}>
            {isSaved ? <BookmarkIcon /> : <BookmarkBorderIcon />}
          </button>
          <button className={styles.actionButton} onClick={handleShare}>
            <ShareIcon />
          </button>
        </div>
      </div>

      <div className={styles.content}>
        <div className={styles.listDescription}>
          <p>{listData.description}</p>
          <div className={styles.listMeta}>
            <span className={styles.lastUpdated}>Last updated: {listData.lastUpdated}</span>
            {listData.createdBy && (
              <span className={styles.createdBy}>By {listData.createdBy}</span>
            )}
          </div>
        </div>

        <div className={styles.tabs}>
          <button 
            className={`${styles.tab} ${activeTab === 'all' ? styles.active : ''}`}
            onClick={() => setActiveTab('all')}
          >
            All ({getTotalItems()})
          </button>
          <button 
            className={`${styles.tab} ${activeTab === 'providers' ? styles.active : ''}`}
            onClick={() => setActiveTab('providers')}
          >
            Providers ({listData.categories.providers.length})
          </button>
          <button 
            className={`${styles.tab} ${activeTab === 'procedures' ? styles.active : ''}`}
            onClick={() => setActiveTab('procedures')}
          >
            Procedures ({listData.categories.procedures.length})
          </button>
          <button 
            className={`${styles.tab} ${activeTab === 'medications' ? styles.active : ''}`}
            onClick={() => setActiveTab('medications')}
          >
            Medications ({listData.categories.medications.length})
          </button>
          <button 
            className={`${styles.tab} ${activeTab === 'articles' ? styles.active : ''}`}
            onClick={() => setActiveTab('articles')}
          >
            Articles ({listData.categories.articles.length})
          </button>
        </div>

        <div className={styles.itemsList}>
          {getFilteredItems().map(renderCard)}
        </div>
      </div>
    </div>
  );
};

export default ListDetail;